import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Building2, ArrowLeft, Printer, Download } from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { FREE_REPORT_GATE_MESSAGE } from "@/utils/planMessaging";

const REPORT_TITLES = {
  rental: "Rental Analysis Report",
  brrrr: "BRRRR Analysis Report",
  flip: "Flip Analysis Report",
  airbnb: "Airbnb Analysis Report",
};
const ANALYSIS_BACK_PATHS = {
  rental: "/rental-analysis",
  brrrr: "/brrrr-calculator",
  flip: "/flip-calculator",
  airbnb: "/airbnb-calculator",
};

const ReportPage = () => {
  const { type, id } = useParams();
  const analysisType = (type || "").toLowerCase();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [reportArtifact, setReportArtifact] = useState(null);
  const [accessState, setAccessState] = useState({ kind: "ok" });
  const reportRef = useRef();
  const generatedOn = reportArtifact?.created_at
    ? new Date(reportArtifact.created_at).toLocaleDateString()
    : new Date().toLocaleDateString();

  const hasValue = (value) => value !== undefined && value !== null && value !== "";

  const formatCurrency = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return "$0";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numeric);
  };

  const formatPercent = (value) => {
    if (value === "Infinite") return "Infinite";
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `${numeric.toFixed(2)}%` : "0.00%";
  };

  const formatRatio = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `${numeric.toFixed(2)}x` : "N/A";
  };

  const getTypeLabel = () => {
    if (analysisType === "brrrr") return "BRRRR Strategy";
    if (analysisType === "airbnb") return "Short-Term Rental";
    if (analysisType === "flip") return "House Flip";
    return "Rental";
  };

  const getPageTitle = () => REPORT_TITLES[analysisType] || "Property Report";

  useEffect(() => {
    if (!["rental", "brrrr", "flip", "airbnb"].includes(analysisType)) {
      toast.error("Unsupported report type");
      navigate("/dashboard");
      return;
    }

    authorizeAndLoadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisType, id, token]);

  const authorizeAndLoadReport = async () => {
    try {
      setLoading(true);
      setAccessState({ kind: "ok" });
      const response = await axios.post(`${API}/reports/from-analysis`, {
        analysis_type: analysisType,
        analysis_id: id,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = response.data || {};
      setReportArtifact(payload);
      setAnalysis(payload.report_payload || null);
    } catch (error) {
      const status = error?.response?.status;
      const responsePayload = error?.response?.data || {};
      const detailPayload = typeof responsePayload?.detail === "object" ? responsePayload.detail : null;
      const payload = detailPayload || responsePayload;
      const paywallTrigger = payload?.paywallTrigger;

      if (status === 403 && paywallTrigger === "REPORT_LIMIT_REACHED") {
        setAccessState({
          kind: "starter_limit",
          message: payload?.message || "Starter users can generate up to 5 new PDF reports per month. Upgrade to Pro for unlimited reports.",
          used: payload?.usage?.reports_this_period ?? 5,
          limit: payload?.usage?.report_limit ?? 5,
        });
        setReportArtifact(null);
        setAnalysis(null);
        return;
      }

      if (status === 403 && paywallTrigger === "REPORTS_PAID_FEATURE") {
        setAccessState({
          kind: "free_blocked",
          message: FREE_REPORT_GATE_MESSAGE,
        });
        setReportArtifact(null);
        setAnalysis(null);
        return;
      }

      if (status === 404) {
        setAccessState({ kind: "not_found", message: "Analysis not found or you do not have access to it." });
        setReportArtifact(null);
        setAnalysis(null);
        return;
      }

      setAccessState({ kind: "error", message: payload?.message || payload?.error || error?.response?.data?.detail || "Failed to load report" });
      setReportArtifact(null);
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => reportRef.current,
    documentTitle: `${analysis?.property_name || "Property"} - ${getPageTitle()}`,
  });

  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;

    toast.info("Generating PDF...");
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#0f172a",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${analysis?.property_name || "Property"}_${analysisType}_Report.pdf`);
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to generate PDF");
    }
  };

  const renderSummaryCards = () => {
    const results = analysis?.results || {};
    let cards = [];

    if (analysisType === "rental") {
      cards = [
        { label: "Purchase Price", value: formatCurrency(results.purchase_price), tone: "text-white" },
        { label: "Monthly Cash Flow", value: formatCurrency(results.monthly_cash_flow), tone: (results.monthly_cash_flow || 0) >= 0 ? "text-emerald-500" : "text-red-500" },
        { label: "Cap Rate", value: formatPercent(results.cap_rate), tone: "text-cyan-500" },
        { label: "Cash-on-Cash", value: formatPercent(results.cash_on_cash_return), tone: "text-amber-500" },
      ];
    } else if (analysisType === "brrrr") {
      cards = [
        { label: "Cash Left in Deal", value: formatCurrency(results.cash_left_in_deal), tone: (results.cash_left_in_deal || 0) <= 0 ? "text-emerald-500" : "text-white" },
        { label: "Cash Out", value: formatCurrency(results.cash_out), tone: "text-cyan-500" },
        { label: "Monthly Cash Flow", value: formatCurrency(results.monthly_cash_flow), tone: (results.monthly_cash_flow || 0) >= 0 ? "text-emerald-500" : "text-red-500" },
        { label: "Equity Captured", value: formatCurrency(results.equity_captured), tone: "text-amber-500" },
      ];
    } else if (analysisType === "flip") {
      cards = [
        { label: "Estimated Profit", value: formatCurrency(results.gross_profit), tone: (results.gross_profit || 0) >= 0 ? "text-emerald-500" : "text-red-500" },
        { label: "ROI", value: formatPercent(results.roi), tone: (results.roi || 0) >= 0 ? "text-cyan-500" : "text-red-500" },
        { label: "Cash Needed", value: formatCurrency(results.cash_needed), tone: "text-white" },
        { label: "70% Rule Max Offer", value: formatCurrency(results.max_offer_70_rule), tone: "text-amber-500" },
      ];
    } else if (analysisType === "airbnb") {
      cards = [
        { label: "Monthly Cash Flow", value: formatCurrency(results.monthly_cash_flow), tone: (results.monthly_cash_flow || 0) >= 0 ? "text-emerald-500" : "text-red-500" },
        { label: "Annual Revenue", value: formatCurrency(results.annual_revenue), tone: "text-cyan-500" },
        { label: "Cap Rate", value: formatPercent(results.cap_rate), tone: "text-purple-500" },
        { label: "Cash-on-Cash", value: formatPercent(results.cash_on_cash), tone: "text-amber-500" },
      ];
    }

    return (
      <div className="mb-8">
        <h2 className="text-xl font-bold text-amber-500 mb-4">Executive Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cards.map((card) => (
            <div key={card.label} className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">{card.label}</div>
              <div className={`text-xl font-bold font-mono ${card.tone}`}>{card.value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRows = (rows) => {
    const filtered = rows.filter((row) => hasValue(row.value));
    if (filtered.length === 0) return null;
    return (
      <div className="space-y-2">
        {filtered.map((row) => (
          <div key={row.label} className={`flex justify-between py-2 border-b border-slate-800 ${row.strong ? "font-semibold" : ""}`}>
            <span className={row.labelClass || "text-slate-400"}>{row.label}</span>
            <span className={`font-mono ${row.valueClass || "text-white"}`}>{row.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderDetailSections = () => {
    const inputs = analysis?.inputs || {};
    const results = analysis?.results || {};

    if (analysisType === "rental") {
      return (
        <>
          <div className="mb-8">
            <h2 className="text-xl font-bold text-amber-500 mb-4">Financial Analysis</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Income and Expenses</h3>
                {renderRows([
                  { label: "Gross Annual Income", value: formatCurrency(results.gross_annual_income) },
                  { label: "Vacancy Loss", value: `-${formatCurrency(results.vacancy_loss)}`, valueClass: "text-red-400" },
                  { label: "Effective Gross Income", value: formatCurrency(results.effective_gross_income) },
                  { label: "Operating Expenses", value: `-${formatCurrency(results.total_operating_expenses)}`, valueClass: "text-red-400" },
                  { label: "Net Operating Income", value: formatCurrency(results.noi), strong: true, labelClass: "text-slate-300", valueClass: "text-emerald-500" },
                ])}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Financing</h3>
                {renderRows([
                  { label: "Down Payment", value: formatCurrency(results.down_payment) },
                  { label: "Loan Amount", value: formatCurrency(results.loan_amount) },
                  { label: "Monthly Mortgage", value: formatCurrency(results.monthly_mortgage) },
                  { label: "Annual Debt Service", value: formatCurrency(results.annual_debt_service) },
                  { label: "Total Investment", value: formatCurrency(results.total_investment), strong: true, labelClass: "text-slate-300", valueClass: "text-amber-500" },
                ])}
              </div>
            </div>
          </div>
          {analysis.projections && analysis.projections.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-amber-500 mb-4">Projection Snapshot</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-xs text-slate-400 uppercase">Year</th>
                      <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase">Rent/Mo</th>
                      <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase">NOI</th>
                      <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase">Cash Flow</th>
                      <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase">Value</th>
                      <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase">Equity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.projections.slice(0, 10).map((proj, index) => (
                      <tr key={index} className="border-b border-slate-800">
                        <td className="py-2 px-3 text-white">{proj.year}</td>
                        <td className="py-2 px-3 text-right font-mono text-slate-300">{formatCurrency(proj.monthly_rent)}</td>
                        <td className="py-2 px-3 text-right font-mono text-slate-300">{formatCurrency(proj.noi)}</td>
                        <td className={`py-2 px-3 text-right font-mono ${(proj.cash_flow || 0) >= 0 ? "text-emerald-500" : "text-red-500"}`}>{formatCurrency(proj.cash_flow)}</td>
                        <td className="py-2 px-3 text-right font-mono text-slate-300">{formatCurrency(proj.property_value)}</td>
                        <td className="py-2 px-3 text-right font-mono text-cyan-500">{formatCurrency(proj.equity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      );
    }

    if (analysisType === "brrrr") {
      return (
        <>
          <div className="mb-8">
            <h2 className="text-xl font-bold text-amber-500 mb-4">BRRRR Performance</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Refinance and Equity</h3>
                {renderRows([
                  { label: "After Repair Value", value: formatCurrency(results.after_repair_value) },
                  { label: "New Loan Amount", value: formatCurrency(results.new_loan_amount) },
                  { label: "Cash Out", value: formatCurrency(results.cash_out), valueClass: "text-emerald-500" },
                  { label: "Cash Left in Deal", value: formatCurrency(results.cash_left_in_deal) },
                  { label: "Equity Captured", value: formatCurrency(results.equity_captured), strong: true, labelClass: "text-slate-300", valueClass: "text-cyan-500" },
                ])}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Income and Return</h3>
                {renderRows([
                  { label: "Monthly Rent", value: formatCurrency(results.monthly_rent) },
                  { label: "Monthly Cash Flow", value: formatCurrency(results.monthly_cash_flow), valueClass: (results.monthly_cash_flow || 0) >= 0 ? "text-emerald-500" : "text-red-500" },
                  { label: "Cap Rate", value: formatPercent(results.cap_rate), valueClass: "text-purple-500" },
                  { label: "DSCR", value: formatRatio(results.dscr), valueClass: Number(results.dscr) >= 1.25 ? "text-emerald-500" : "text-amber-500" },
                  { label: "Cash-on-Cash Return", value: formatPercent(results.cash_on_cash_return), strong: true, labelClass: "text-slate-300", valueClass: "text-amber-500" },
                ])}
              </div>
            </div>
          </div>
          {results.infinite_return && (
            <div className="mb-8 bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-4">
              <p className="text-emerald-400 font-semibold">Infinite Return Scenario: cash fully recycled with positive cash flow.</p>
            </div>
          )}
        </>
      );
    }

    if (analysisType === "flip") {
      return (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-amber-500 mb-4">Flip Breakdown</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Costs</h3>
              {renderRows([
                { label: "Purchase Price", value: formatCurrency(results.purchase_price) },
                { label: "Closing Costs (Buy)", value: formatCurrency(results.closing_costs_buy) },
                { label: "Rehab Budget", value: formatCurrency(results.rehab_budget) },
                { label: "Contingency", value: formatCurrency(results.contingency) },
                { label: "Holding Costs", value: formatCurrency(results.holding_costs) },
                { label: "Financing Costs", value: formatCurrency(results.financing_costs) },
                { label: "Total Costs", value: formatCurrency(results.total_costs), strong: true, labelClass: "text-slate-300", valueClass: "text-amber-500" },
              ])}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Sale and Return</h3>
              {renderRows([
                { label: "ARV", value: formatCurrency(results.arv) },
                { label: "Net Sale Price", value: formatCurrency(results.net_sale_price) },
                { label: "Estimated Profit", value: formatCurrency(results.gross_profit), valueClass: (results.gross_profit || 0) >= 0 ? "text-emerald-500" : "text-red-500" },
                { label: "ROI", value: formatPercent(results.roi), valueClass: (results.roi || 0) >= 0 ? "text-cyan-500" : "text-red-500" },
                { label: "Cash ROI", value: formatPercent(results.cash_roi), valueClass: (results.cash_roi || 0) >= 0 ? "text-purple-500" : "text-red-500" },
                { label: "Cash Needed", value: formatCurrency(results.cash_needed) },
                { label: "70% Rule Max Offer", value: formatCurrency(results.max_offer_70_rule), strong: true, labelClass: "text-slate-300", valueClass: "text-amber-500" },
              ])}
            </div>
          </div>
          {hasValue(inputs.purchase_price) && hasValue(results.max_offer_70_rule) && (
            <div className="mt-6 bg-slate-800/50 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">70% Rule Status</div>
              <div className={`text-sm font-semibold ${Number(inputs.purchase_price) <= Number(results.max_offer_70_rule) ? "text-emerald-400" : "text-amber-400"}`}>
                {Number(inputs.purchase_price) <= Number(results.max_offer_70_rule)
                  ? "Offer is within 70% rule guidance."
                  : "Offer is above 70% rule guidance."}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (analysisType === "airbnb") {
      return (
        <>
          <div className="mb-8">
            <h2 className="text-xl font-bold text-amber-500 mb-4">STR Performance</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Income and Occupancy</h3>
                {renderRows([
                  { label: "Annual Revenue", value: formatCurrency(results.annual_revenue), valueClass: "text-cyan-500" },
                  { label: "Avg Occupancy", value: formatPercent(results.avg_occupancy) },
                  { label: "Avg Nightly Rate", value: formatCurrency(results.avg_nightly_rate) },
                  { label: "Break-even Occupancy", value: formatPercent(results.break_even_occupancy), valueClass: "text-amber-500" },
                  { label: "Annual NOI", value: formatCurrency(results.noi), strong: true, labelClass: "text-slate-300", valueClass: "text-emerald-500" },
                ])}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Investment Metrics</h3>
                {renderRows([
                  { label: "Down Payment", value: formatCurrency(results.down_payment) },
                  { label: "Monthly Mortgage", value: formatCurrency(results.monthly_mortgage) },
                  { label: "Annual Expenses", value: formatCurrency(results.annual_expenses), valueClass: "text-red-400" },
                  { label: "Cap Rate", value: formatPercent(results.cap_rate), valueClass: "text-purple-500" },
                  { label: "Cash-on-Cash", value: formatPercent(results.cash_on_cash), valueClass: "text-amber-500" },
                  { label: "Annual Cash Flow", value: formatCurrency(results.annual_cash_flow), strong: true, labelClass: "text-slate-300", valueClass: (results.annual_cash_flow || 0) >= 0 ? "text-emerald-500" : "text-red-500" },
                ])}
              </div>
            </div>
          </div>
          {analysis.monthly_breakdown && analysis.monthly_breakdown.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-amber-500 mb-4">Monthly Breakdown</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 text-xs text-slate-400 uppercase">Month</th>
                      <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase">Occ.</th>
                      <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase">Nightly</th>
                      <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase">Gross</th>
                      <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.monthly_breakdown.map((month, index) => (
                      <tr key={index} className="border-b border-slate-800">
                        <td className="py-2 px-3 text-white">{month.month}</td>
                        <td className="py-2 px-3 text-right font-mono text-slate-300">{formatPercent(month.occupancy_rate)}</td>
                        <td className="py-2 px-3 text-right font-mono text-slate-300">{formatCurrency(month.nightly_rate)}</td>
                        <td className="py-2 px-3 text-right font-mono text-slate-300">{formatCurrency(month.gross_income)}</td>
                        <td className={`py-2 px-3 text-right font-mono ${(month.net_income || 0) >= 0 ? "text-emerald-500" : "text-red-500"}`}>{formatCurrency(month.net_income)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  if (accessState.kind !== "ok") {
    const backPath = ANALYSIS_BACK_PATHS[analysisType] ? `${ANALYSIS_BACK_PATHS[analysisType]}/${id}` : "/dashboard";
    const title = accessState.kind === "starter_limit"
      ? "Monthly Report Limit Reached"
      : accessState.kind === "free_blocked"
        ? "Reports Are a Paid Feature"
        : accessState.kind === "not_found"
          ? "Report Unavailable"
          : "Unable to Load Report";

    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <div className="max-w-xl w-full glass-card rounded-xl border border-slate-800 p-8">
          <h1 className="text-2xl font-semibold text-white mb-3">{title}</h1>
          {accessState.kind === "starter_limit" && (
            <p className="text-slate-300 mb-2">{accessState.used} of {accessState.limit} reports used this month.</p>
          )}
          <p className="text-slate-400 mb-6">{accessState.message}</p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => navigate("/upgrade")} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
              Upgrade to Pro
            </Button>
            <Button variant="outline" onClick={() => navigate(backPath)} className="border-slate-700 text-slate-300 hover:text-white">
              Back to analysis
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Analysis not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="report-page">
      <header className="glass border-b border-slate-800 sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-slate-400 hover:text-white">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">{getPageTitle()}</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              <Button variant="outline" onClick={handlePrint} className="border-slate-700 text-slate-300 hover:text-white" data-testid="print-btn">
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button onClick={handleDownloadPDF} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="download-pdf-btn">
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div ref={reportRef} className="bg-slate-900 rounded-xl overflow-hidden" style={{ padding: "32px" }}>
          <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <Building2 className="h-10 w-10 text-amber-500" />
              <div>
                <div className="text-2xl font-bold text-white font-['Manrope']">{analysis.property_name}</div>
                <div className="text-slate-400">{analysis.inputs?.address || `${getTypeLabel()} Investment Analysis`}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-400">Generated</div>
              <div className="text-white font-mono">{generatedOn}</div>
            </div>
          </div>

          {renderSummaryCards()}
          {renderDetailSections()}

          <div className="pt-6 border-t border-slate-800 mt-8">
            <div className="flex items-center justify-between text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-amber-500" />
                <span>Flowguard</span>
              </div>
              <div>
                Report generated for {user?.name} - {generatedOn}
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-600">
              This report is for informational purposes only and should not be considered financial advice.
              Please consult qualified professionals before making investment decisions.
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReportPage;
